import {
    ZfCard,
    ZfCardMedia,
    ZfCardMediaHeader,
    ZfDialog,
    ZfDialogContent,
    ZfDialogHeader,
    ZfDivider,
    ZfFormField,
    ZfInput,
    ZfStack,
    ZfTextButton,
    ZfTypography,
} from '@zafire/react';
import type { FunctionComponent } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getImageUrlByString } from '../../../../styles/assets/imageMap';
import type { TopLevelTopicDto } from '../../../api/generated/client';
import type { EventWithDefaultDate } from '../../../interfaces/event';
import type { IMap } from '../../../interfaces/generic';
import { List } from '../../atoms';

interface IProps {
    topics: TopLevelTopicDto[];
    selectedTopic?: TopLevelTopicDto;
    topicEventMap: IMap<EventWithDefaultDate[]>;
    onTopicSelected: (topic?: TopLevelTopicDto) => void;
    onEventSelected: (event: EventWithDefaultDate) => void;
    show: boolean;
    onClose: () => void;
}

export const SelectEventModal: FunctionComponent<IProps> = ({ topics, selectedTopic, onEventSelected, topicEventMap, onClose, onTopicSelected, show }) => {
    const { t, i18n } = useTranslation();
    const [selectedEvent, setSelectedEvent] = useState<EventWithDefaultDate | undefined>(undefined);
    const [selectedEventIndex, setSelectedEventIndex] = useState<number | undefined>(-1);
    const [customEventTitle, setCustomEventTitle] = useState<string | undefined>(undefined);

    const onTopicClick = (topic: TopLevelTopicDto, selected: boolean) => {
        const topic_ = selected ? topic : undefined;
        setSelectedEventIndex(-1);
        setCustomEventTitle(undefined);
        setSelectedEvent(undefined);
        onTopicSelected(topic_);
    };

    const onEventClick = (event: EventWithDefaultDate, index: number) => {
        if (selectedEventIndex !== -1 && selectedEventIndex === index) {
            setSelectedEventIndex(undefined);
            setSelectedEvent(undefined);
            setCustomEventTitle(undefined);
        } else {
            setSelectedEventIndex(index);
            setSelectedEvent(event);
        }
    };

    const onDialogConfirm = () => {
        if (selectedEvent) {
            onEventSelected(selectedEvent);
        } else {
            const event: EventWithDefaultDate = {
                translation: {},
                defaultReminderDateOffset: {
                    months: 0,
                    days: 0,
                },
            };
            event.translation[i18n.language] = customEventTitle!;
            onEventSelected(event);
        }
    };

    return (
        <ZfDialog show={show} onDialogClose={onClose} size="m" className="select-topic-modal">
            <ZfDialogHeader>{t('zfEventModalNewEvent')}</ZfDialogHeader>
            <ZfDialogContent>
                <ZfStack direction="row" spacing="extra-large">
                    <ZfStack direction="column" spacing="extra-small" style={{ maxHeight: '60vh', overflowY: 'scroll', overflowX: 'hidden' }}>
                        {topics.map((topic) => (
                            <ZfCard
                                key={topic.id}
                                className="action-dialog-selectable-card hidden"
                                onCardSelect={(event: CustomEvent<boolean>) => onTopicClick(topic, event.detail)}
                                selectable={true}
                                selected={selectedTopic?.id === topic.id}
                            >
                                <ZfCardMedia image={getImageUrlByString(topic.key)} style={{ height: '140px' }}>
                                    <ZfCardMediaHeader>
                                        <ZfTypography slot="leading" variant="body-large" color="invert">
                                            {topic.name[i18n.language]}
                                        </ZfTypography>
                                    </ZfCardMediaHeader>
                                </ZfCardMedia>
                            </ZfCard>
                        ))}
                    </ZfStack>
                    <ZfStack direction="column" spacing="extra-large" justify="space-between" style={{ flex: 1 }}>
                        {selectedTopic?.key && topicEventMap[selectedTopic.key] && (
                            <List
                                title={selectedTopic.name[i18n.language]}
                                items={topicEventMap[selectedTopic.key].map((event) => ({
                                    title: event.translation[i18n.language],
                                    data: event,
                                }))}
                                selectedIndex={selectedEventIndex}
                                onItemClick={onEventClick}
                                hasBorderBottom={(index) => !!selectedTopic?.key && index < topicEventMap[selectedTopic.key].length - 1}
                            />
                        )}
                        <ZfStack direction="column" spacing="small">
                            <ZfDivider />
                            {!!selectedTopic && !selectedEvent && (
                                <ZfFormField label={t('otherEventTitle')}>
                                    <ZfInput
                                        placeholder={t('chooseATitle')}
                                        value={customEventTitle}
                                        onInputChange={(event) => setCustomEventTitle(event.detail)}
                                        multiline={true}
                                        counter={true}
                                        maxLength={75}
                                        rows={2}
                                    />
                                </ZfFormField>
                            )}
                            <ZfStack alignment="flex-end">
                                <ZfTextButton disabled={!selectedEvent && !customEventTitle} onClick={onDialogConfirm}>
                                    {t('eventModalNextButton')}
                                </ZfTextButton>
                            </ZfStack>
                        </ZfStack>
                    </ZfStack>
                </ZfStack>
            </ZfDialogContent>
        </ZfDialog>
    );
};
